import { useCallback } from 'react'

import { usePermissions } from '../usePermissions/usePermissions'

import {
  accountDomainPermissions,
  domainsCoveringAccount,
  domainsModifyingAccount,
  type AccountTenancy
} from '@percent/domain/iam'

/** The account actions pbp-api checks through `canModifyAccount`. */
export type AccountModifyAction = 'can_delete' | 'can_redact' | 'can_reactivate' | 'can_reset_2fa'

/**
 * Whether the signed-in user can see or act on an account, decided as pbp-api decides it: through
 * any domain covering (or modifying) that account, from its partner, organisation and admin fields.
 * pbp-api also lets a tenant super admin act on any account, which the permissions list can't show.
 */
export function useAccountAccess() {
  const { userHasPermissions } = usePermissions()

  const canViewAccount = useCallback(
    (account: AccountTenancy) =>
      userHasPermissions({ any: accountDomainPermissions(domainsCoveringAccount(account), 'can_view') }),
    [userHasPermissions]
  )

  const canModifyAccount = useCallback(
    (account: AccountTenancy, action: AccountModifyAction) =>
      userHasPermissions({ any: accountDomainPermissions(domainsModifyingAccount(account), action) }),
    [userHasPermissions]
  )

  return { canViewAccount, canModifyAccount }
}
